<script setup>
import { computed, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import DetailMapMacroCardComponent from '@/components/detail/data/DetailMapMacroCardComponent.vue'
import DetailMapMicroCardComponent from '@/components/detail/data/DetailMapMicroCardComponent.vue'
import MapComponent from '@/components/map/MapComponent.vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import {
  DETAIL_INFRAS_MAP_FEATURE_LAYER,
  DETAIL_INFRAS_MAP_INFO_LAYERS,
  DETAIL_LANDBOARD_MAP_FEATURE_LAYER,
  DETAIL_LANDBOARD_MAP_INFO_LAYERS,
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MAXZOOM,
  MAP_MINZOOM,
  MAP_PROJECTION,
} from '@/constants'
import { createGeoJsonFeature } from '@/services/map/FeatureService'
import { transform } from '@/services/map/ProjectionService'
import { useDetailStore } from '@/stores/useDetailStore'
import { useLandBoardStore } from '@/stores/useLandBoardStore'

/**
 * Off-screen maps that the QV Short Report dialog captures via `captureAll()`
 * and ships to the backend (base64 PNG → PHPWord `setImageValue`).
 *
 * Matching the Expert flow (Detail page):
 *   - mikro / makro: mount the actual DetailMap*CardComponent cards. They
 *     share `useMicroMacroDetailMap` which reads `ehrId` + `ehrGeometry`
 *     from `useDetailStore` — QuickValuationResult populates these during
 *     setup so they're present when these cards mount.
 *   - kataster / infra: not exposed as standalone Detail cards. Reimplement
 *     inline here with the same constants the Detail DataTab uses
 *     (`DETAIL_LANDBOARD_MAP_INFO_LAYERS`, `DETAIL_INFRAS_MAP_INFO_LAYERS`).
 *     We can't reuse `DetailLandBoardComponent` / `DetailInfraComponent` directly
 *     — they fire API side-effects on mount.
 *
 * Wrapper is positioned off-screen but kept at real pixel dimensions —
 * `display:none` would prevent OpenLayers from initialising its canvas, so
 * we use absolute positioning + `visibility:hidden` + `aria-hidden`.
 */

const props = defineProps({
  ehrData: {
    type: Object,
    default: () => ({}),
  },
  ehrId: {
    type: [String, Number],
    default: null,
  },
})

const { findLayer } = useDetailMapHelpers()
const { landBoard } = storeToRefs(useLandBoardStore())
const { ehrGeometry } = storeToRefs(useDetailStore())

const microCardRef = useTemplateRef('microCardRef')
const macroCardRef = useTemplateRef('macroCardRef')
const katasterMapRef = useTemplateRef('katasterMapRef')
const infraMapRef = useTemplateRef('infraMapRef')

const mapview = {
  extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
  minZoom: MAP_MINZOOM,
  maxZoom: MAP_MAXZOOM,
  projection: MAP_PROJECTION,
  center: transform('fromLonLat', MAP_CENTER, MAP_PROJECTION),
}

// ---- Kataster + Infra overlays (inline maps) ----

const katasterOverlay = shallowRef(null)
const infraOverlay = shallowRef(null)
const sharedBbox = shallowRef(null)

const katasterBaseLayers = computed(() => [findLayer('ma-photo')])
const katasterInfoLayers = computed(() =>
  DETAIL_LANDBOARD_MAP_INFO_LAYERS.map((id) => findLayer(id)),
)
const infraBaseLayers = computed(() => [findLayer('ma-map')])
const infraInfoLayers = computed(() =>
  DETAIL_INFRAS_MAP_INFO_LAYERS.map((id) => findLayer(id)),
)

const buildOverlay = (featureLayerId) => {
  const geometry = landBoard.value?.geometry
    ? [landBoard.value.geometry]
    : Array.isArray(ehrGeometry.value)
      ? ehrGeometry.value
      : Array.isArray(props.ehrData?.geometry)
        ? props.ehrData.geometry
        : []

  if (!geometry.length) return null

  const layer = findLayer(featureLayerId)
  if (!layer) return null

  geometry.forEach((geom) => {
    const feature = createGeoJsonFeature(geom, { id: props.ehrId })
    if (feature) layer.features.push(feature)
  })
  return layer
}

const applyOverlays = () => {
  katasterOverlay.value = buildOverlay(DETAIL_LANDBOARD_MAP_FEATURE_LAYER)
  infraOverlay.value = buildOverlay(DETAIL_INFRAS_MAP_FEATURE_LAYER)
  sharedBbox.value = landBoard.value?.bbox ?? null
}

onMounted(applyOverlays)

// Re-apply when landBoard lands async (Inertia deferred / parent re-sync).
watch(() => [landBoard.value, ehrGeometry.value], applyOverlays, { deep: false })

watch([katasterOverlay, infraOverlay, sharedBbox], () => {
  if (katasterMapRef.value) {
    if (katasterOverlay.value) katasterMapRef.value.updateOverlay?.(katasterOverlay.value)
    if (sharedBbox.value) katasterMapRef.value.updateBbox?.(sharedBbox.value)
  }
  if (infraMapRef.value) {
    if (infraOverlay.value) infraMapRef.value.updateOverlay?.(infraOverlay.value)
    if (sharedBbox.value) infraMapRef.value.updateBbox?.(sharedBbox.value)
  }
})

// ---- Capture ----

// Detail cards' `exportMap` writes to `detailStore.mapExport[key]` (see
// useMicroMacroDetailMap.js:68-75) rather than returning the dataUri. Trigger
// the export, then read the freshly-written value from the store.
const detailStore = useDetailStore()

const captureCard = async (cardRef, storeKey) => {
  if (!cardRef.value?.exportMap) return ''
  try {
    await cardRef.value.exportMap()
  } catch (err) {
    console.warn('[QvHiddenMaps] card exportMap failed', storeKey, err)
    return ''
  }
  return detailStore.mapExport?.[storeKey] || ''
}

/**
 * For inline kataster/infra maps. MapComponent doesn't watch its `overlay` /
 * `bbox` props post-mount, so without an explicit updateOverlay call the map
 * stays at default zoom=3 and captures a blank Estonia-wide frame. Push the
 * current overlay + bbox right before the capture so the `zoomToFeatures`
 * re-fit in updateOverlay runs.
 *
 * RED-210: No more arbitrary 350ms sleep — MapComponent.exportMap now flushes
 * the nextTick-scheduled fitToLayer internally and only resolves once
 * `rendercomplete` fires (which OL emits after all tiles settle).
 */
const captureInline = async (mapRef, overlay, extent) => {
  if (!mapRef.value) return ''
  if (overlay && mapRef.value.updateOverlay) {
    mapRef.value.updateOverlay(overlay)
  }
  if (extent && mapRef.value.updateBbox) {
    mapRef.value.updateBbox(extent)
  }

  if (!mapRef.value.exportMap) return ''
  try {
    return await mapRef.value.exportMap()
  } catch (err) {
    console.warn('[QvHiddenMaps] inline exportMap failed', err)
    return ''
  }
}

const LOCATION_MAP_COUNT = 4

const captureAll = async (onProgress) => {
  // Micro/macro cards write to detailStore; inline kataster/infra return directly.
  // Tiles are already cached by the time the user clicks Generate (the hidden
  // maps mount on page load), so parallelize the inline pair too — there's no
  // WMS saturation to worry about post-mount.
  const total = LOCATION_MAP_COUNT
  let completed = 0
  const tick = (label) => {
    completed += 1
    if (typeof onProgress === 'function') {
      onProgress({ completed, total, label })
    }
  }

  const wrap = (promise, label) => promise.then((value) => {
    tick(label)
    return value
  })

  const [mikro, makro, kataster, infra] = await Promise.all([
    wrap(captureCard(microCardRef, 'micro'), 'mikro'),
    wrap(captureCard(macroCardRef, 'macro'), 'makro'),
    wrap(captureInline(katasterMapRef, katasterOverlay.value, sharedBbox.value), 'kataster'),
    wrap(captureInline(infraMapRef, infraOverlay.value, sharedBbox.value), 'infra'),
  ])

  return { mikro, makro, kataster, infra }
}

defineExpose({ captureAll, total: LOCATION_MAP_COUNT })
</script>

<template>
  <div
    aria-hidden="true"
    class="qv-hidden-maps"
    style="position: absolute; left: -10000px; top: -10000px; width: 720px; height: 540px; visibility: hidden; pointer-events: none;"
  >
    <div class="qv-map-slot">
      <DetailMapMicroCardComponent ref="microCardRef" />
    </div>
    <div class="qv-map-slot">
      <DetailMapMacroCardComponent ref="macroCardRef" />
    </div>
    <div class="qv-map-slot">
      <MapComponent
        ref="katasterMapRef"
        :center="mapview.center"
        :extent="mapview.extent"
        :zoom="3"
        :bbox="sharedBbox"
        :min-zoom="mapview.minZoom"
        :max-zoom="mapview.maxZoom"
        :projection="mapview.projection"
        :base-layers="katasterBaseLayers"
        :info-layers="katasterInfoLayers"
        :overlay="katasterOverlay"
        :zoom-to-features="true"
        :zoom-control="false"
      />
    </div>
    <div class="qv-map-slot">
      <MapComponent
        ref="infraMapRef"
        :center="mapview.center"
        :extent="mapview.extent"
        :zoom="3"
        :bbox="sharedBbox"
        :min-zoom="mapview.minZoom"
        :max-zoom="mapview.maxZoom"
        :projection="mapview.projection"
        :base-layers="infraBaseLayers"
        :info-layers="infraInfoLayers"
        :overlay="infraOverlay"
        :zoom-to-features="true"
        :zoom-control="false"
      />
    </div>
  </div>
</template>

<style scoped>
/* 720×540 matches the original pre-RED-210 slot dimensions. Note the actual
 * captured canvas is LARGER than the slot in practice:
 *   - Kataster / infra render at 900×540 because `.map { min-width: 900px }`
 *     forces the width; height fills the slot (5:3 aspect).
 *   - Micro / macro use DetailMap*CardComponent which hardcodes
 *     `.detail-map { height: 600px }`, so those render at 900×600 (3:2 aspect).
 * Shrinking the slot changed the CSS viewport OpenLayers fits to — that broke
 * the zoom level (features too small) and masked the landboard red stroke on
 * ESG maps. Payload savings now come purely from JPEG compression on ESG
 * (~5–10× smaller) and the multipart upload (33% less transit vs. base64). */
.qv-map-slot {
  width: 720px;
  height: 540px;
  margin-bottom: 10px;
}
</style>
